import { consumeDocumentReload } from './documentReloadBudget';

/**
 * In-document re-import backoff before the budgeted document reload.
 *
 * Kept deliberately SHORT. Chromium memoises a failed
 * module fetch in the document's module map: once a route chunk — or any
 * static dependency of it — has failed, every later `import()` of the same
 * specifier in that document rejects again WITHOUT touching the network.
 * Measured on CI (targeted-e2e 36214310842): one aborted request for the
 * OperationsPlanDraftBoundary chunk produced ZERO further requests across the
 * old 11.2s retry schedule; the route recovered only when the reload fired,
 * so the owner stared at a blank full-screen spinner for 11.2s after a single
 * transient drop (net::ERR_NETWORK_CHANGED on a busy host is enough).
 *
 * The document reload is the recovery that actually refetches. These two
 * short re-imports remain only for engines that do retry a failed module
 * fetch; in Chromium they cost ~1s, not 11s.
 */
const RETRY_DELAYS_MS = [250, 750] as const;

type LazyModule<T> = {
  default?: T;
};

const delay = (ms: number): Promise<void> =>
  new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });

const missingDefaultError = (): Error =>
  new Error('Lazy-loaded module resolved without a default export');

export async function lazyWithRetry<T>(
  factory: () => Promise<LazyModule<T>>
): Promise<{ default: T }> {
  let lastError: unknown;

  // A couple of quick targeted re-imports first (see RETRY_DELAYS_MS: they
  // cannot refetch in Chromium, whose module map memoises the failure), then
  // the budgeted document reload, which is the recovery that refetches.
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const module = await factory();

      if (module.default === undefined) {
        throw missingDefaultError();
      }

      return { default: module.default };
    } catch (error) {
      lastError = error;

      const delayMs = RETRY_DELAYS_MS[attempt];
      if (delayMs !== undefined) {
        await delay(delayMs);
      }
    }
  }

  // Last resort: reload the document — from the SHARED budget the boot watchdog
  // in index.html draws on too. A network episode bad enough to kill a route
  // chunk here has often already cost a boot reload, and four reloads of one
  // document is not recovery, it is a loop the owner cannot escape.
  if (typeof window !== 'undefined' && consumeDocumentReload()) {
    window.location.reload();
    return new Promise(() => {});
  }

  throw lastError;
}
